import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getEmbedUrl, isDirectVideoUrl } from '@/utils/videoUtils';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { supabase } from '@/integrations/supabase/client';
import { Video } from 'lucide-react';

interface SiblingLesson {
  id: string;
  title: string;
  video_url: string;
}

interface MenuTutorialVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  videoUrl: string;
  menuItemLabel: string;
  lessonId?: string;
  onWatched?: () => void;
}

export function MenuTutorialVideoModal({ 
  isOpen, 
  onClose, 
  title, 
  videoUrl,
  menuItemLabel,
  lessonId,
  onWatched
}: MenuTutorialVideoModalProps) {
  const [currentVideoUrl, setCurrentVideoUrl] = useState(videoUrl);
  const [currentTitle, setCurrentTitle] = useState(title);
  const [currentLessonId, setCurrentLessonId] = useState(lessonId);
  const [siblingLessons, setSiblingLessons] = useState<SiblingLesson[]>([]);

  const embedUrl = getEmbedUrl(currentVideoUrl);
  const isDirectVideo = isDirectVideoUrl(currentVideoUrl);

  // Reset to original video when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentVideoUrl(videoUrl);
      setCurrentTitle(title);
      setCurrentLessonId(lessonId);
    }
  }, [isOpen, videoUrl, title, lessonId]);

  // Fetch sibling lessons from the same module
  useEffect(() => {
    async function fetchSiblingLessons() {
      if (!currentLessonId || !isOpen) {
        setSiblingLessons([]);
        return;
      }

      // First get the current lesson's module_id
      const { data: currentLesson, error: lessonError } = await supabase
        .from('os_lms_lessons')
        .select('module_id')
        .eq('id', currentLessonId)
        .single();

      if (lessonError || !currentLesson?.module_id) {
        setSiblingLessons([]);
        return;
      }

      // Fetch all lessons in the same module
      const { data: lessons, error: siblingsError } = await supabase
        .from('os_lms_lessons')
        .select('id, title, content, sort_order')
        .eq('module_id', currentLesson.module_id)
        .neq('id', currentLessonId)
        .order('sort_order', { ascending: true });

      if (siblingsError || !lessons) {
        setSiblingLessons([]);
        return;
      }

      // Filter to lessons with video_url and map to SiblingLesson
      const siblings: SiblingLesson[] = lessons
        .filter(l => {
          const content = l.content as { video_url?: string } | null;
          return content?.video_url;
        })
        .map(l => {
          const content = l.content as { video_url: string };
          return {
            id: l.id,
            title: l.title,
            video_url: content.video_url,
          };
        });

      setSiblingLessons(siblings);
    }

    fetchSiblingLessons();
  }, [currentLessonId, isOpen]);

  const handleClose = () => {
    if (onWatched) {
      onWatched();
    }
    onClose();
  };

  const handleSiblingClick = (sibling: SiblingLesson) => {
    setCurrentVideoUrl(sibling.video_url);
    setCurrentTitle(sibling.title);
    setCurrentLessonId(sibling.id);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug
            popupName="Menu Tutorial Video"
            popupContext={`/tutorial/${menuItemLabel}`}
            isOpen={isOpen}
          >
            {currentTitle || `Tutorial: ${menuItemLabel}`}
          </DialogTitleWithBug>
        </DialogHeader>
        
        <div className="aspect-video w-full rounded-lg overflow-hidden bg-muted">
          {isDirectVideo ? (
            <video
              src={currentVideoUrl}
              controls
              className="w-full h-full object-contain"
              autoPlay
            />
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={currentTitle}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Video not available
            </div>
          )}
        </div>

        {/* Related videos from same module */}
        {siblingLessons.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">More videos in this module</h4>
            <div className="flex flex-wrap gap-2">
              {siblingLessons.map((sibling) => (
                <button
                  key={sibling.id}
                  onClick={() => handleSiblingClick(sibling)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    sibling.id === currentLessonId
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 hover:bg-muted border-border hover:border-primary/50'
                  }`}
                >
                  <Video className="h-3 w-3" />
                  <span className="truncate max-w-[200px]">{sibling.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button onClick={handleClose}>
            Got it!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
